/**
 * Global download indicator (#194).
 *
 * A multi-GB Unreal build can take many minutes. Before this, the only
 * feedback lived INSIDE the launcher popover (which closes on outside
 * click) or as a one-shot toast on a trainee card — so a user who pressed
 * Launch and navigated away saw nothing, read it as "nothing happened",
 * and pressed again. This banner is mounted once in the chrome, so it is
 * page-independent, and it stays up for the whole download.
 *
 * It lingers briefly on a terminal status so a finish or a failure is
 * actually seen — a download that completes while the user is on another
 * page would otherwise vanish without ever having been visible.
 */
import { useEffect, useRef, useState } from 'react';
import { useLauncherReadiness } from '../hooks/useLauncherReadiness';

/** How long a Completed / Failed download stays on screen. */
const LINGER_MS = 8000;

export function DownloadBanner() {
  // The download indicator is channel-independent (any version of the app);
  // the label is the version actually downloading, not the Dev channel's.
  const { appDownload, downloadActive, appDownloadVersionLabel: versionLabel } = useLauncherReadiness('Dev');
  const status = appDownload?.status;
  const terminal = status === 'Completed' || status === 'Failed';
  const [showTerminal, setShowTerminal] = useState(false);
  // Keyed on (versionId, status) so a NEW terminal result re-opens the
  // linger window instead of being swallowed by the previous one.
  const terminalKey = terminal ? `${String(appDownload?.versionId)}:${status}` : null;
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (!terminalKey || terminalKey === lastKey.current) return;
    lastKey.current = terminalKey;
    setShowTerminal(true);
    const t = setTimeout(() => setShowTerminal(false), LINGER_MS);
    return () => clearTimeout(t);
  }, [terminalKey]);

  if (!appDownload) return null;
  if (!downloadActive && !(terminal && showTerminal)) return null;

  const failed = status === 'Failed';
  const done = status === 'Completed';
  const label = versionLabel ? `MineZero v${versionLabel}` : 'MineZero';
  const pct = appDownload.percent;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 18,
        zIndex: 60,
        minWidth: 300,
        maxWidth: 'min(520px, 92vw)',
        background: 'var(--panel)',
        border: `1px solid ${failed ? 'var(--bad-line, var(--bad))' : 'var(--border)'}`,
        borderRadius: 'var(--r, 8px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        padding: '10px 14px',
        display: 'grid',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
        <b style={{ color: failed ? 'var(--bad)' : 'var(--text)' }}>
          {failed
            ? `${label} — download failed`
            : done
              ? `${label} downloaded, launching…`
              : `Downloading ${label}`}
        </b>
        {!failed && !done && pct != null ? (
          <span style={{ marginLeft: 'auto', color: 'var(--muted)' }}>{pct}%</span>
        ) : null}
      </div>

      {!failed && !done ? (
        <div
          style={{
            height: 5,
            borderRadius: 3,
            background: 'var(--panel-3, var(--panel-2))',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: pct != null ? `${pct}%` : '35%',
              height: '100%',
              background: 'var(--amber, var(--good))',
              // Indeterminate until the launcher knows the total size.
              animation: pct == null ? 'pulse 1.4s ease-in-out infinite' : undefined,
              transition: 'width .3s linear',
            }}
          />
        </div>
      ) : null}

      <div style={{ fontSize: 11, color: failed ? 'var(--bad)' : 'var(--muted)' }}>
        {failed
          ? (appDownload.errorMessage ?? 'The launcher did not say why.')
          : (appDownload.statusMessage ??
            (done ? 'The launcher starts the app on its own.' : 'Keep this tab open.'))}
      </div>
    </div>
  );
}
